import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Barcode from "react-barcode";
import { Loader2, Printer, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CR_W = 766;
const CR_H = 1096;
const PAGE_W = 794;
const PAGE_H = 1123;
const SX = PAGE_W / CR_W;
const SY = PAGE_H / CR_H;

// ── Margen seguro de impresión (10mm ≈ 38px) ──
// La mayoría de las impresoras no imprimen los primeros ~5-10mm de cada borde.
// Aplicamos un padding interno y escalamos el contenido para que entre completo.
const MARGIN = 32; // px de margen seguro en cada lado
const SAFE_W = PAGE_W - (MARGIN * 2);  // ancho útil
const SAFE_H = PAGE_H - (MARGIN * 2);  // alto útil
const PRINT_SCALE = Math.min(SAFE_W / PAGE_W, SAFE_H / PAGE_H); // ~0.92

// ══════════════════════════════════════════════════════════════
// UTILIDADES
// ══════════════════════════════════════════════════════════════
const fmt = (v) =>
  new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(parseFloat(v) || 0);

const fmtFecha = (s) => {
  if (!s || s === "1900-01-01" || s === "0000-00-00") return "-";
  const p = String(s).substring(0, 10).split("-");
  if (p.length !== 3) return s;
  return `${p[2]}/${p[1]}/${p[0]}`;
};

const getTitulo = (tipo, periodo, concepto) => {
  const t = String(tipo || "").toUpperCase().trim();
  const anio = String(periodo || "").substring(0, 4) || new Date().getFullYear();
  if (t === "A") return `PAGO ANUAL ${anio}`;
  if (t === "1") return `PAGO 1° SEMESTRE ${anio}`;
  if (t === "2") return `PAGO 2° SEMESTRE ${anio}`;
  // Tipo B y otros: mostrar período formateado (ej: 202101 → "CUOTA 01/2021")
  if (periodo && periodo.length >= 6) {
    const mes = String(periodo).substring(4, 6);
    return `CUOTA ${mes}/${anio}`;
  }
  return `PAGO PERÍODO ${periodo || ""}`;
};

const getCodCuerpo = (boleta) => {
  const m = String(boleta.CODBARRASM || "").trim();
  // Mensuales: CODBARRASM viene como "*P3484206*" → extraer "P" + padded a 10
  if (m.startsWith("*") && m.endsWith("*")) {
    const inner = m.replace(/\*/g, "");
    const letter = inner.charAt(0);
    const num = inner.substring(1);
    return `${letter}${num.padStart(10, "0")}`;
  }
  // Si tiene valor no vacío y útil, usar directo
  if (m && m.length > 5) return m;
  // Anuales/Semestrales: construir MIM...{AÑO}
  const n = String(boleta.NOMENCLA || "").replace(/-/g, "");
  const a = String(boleta.CUOTA || boleta.PERIODO || "").substring(0, 4);
  return `M${n}${a}`;
};

const getCodInterbancario = (boleta) => {
  const c = String(boleta.CODBARRASP || "").trim();
  return /^\d{20,}$/.test(c) ? c : null;
};

const getCodPagoElec = (n) => String(n || "").replace(/-/g, "");

/**
 * Calcula bw (ancho de barra) y fs (font size) para react-barcode
 * según la longitud del valor a codificar y el ancho disponible en px.
 * Evita que el barcode se comprima o desborde entre tipos de boleta.
 *
 * En CODE39 cada carácter ocupa aprox. 13 módulos (9 barras + espacios + quiet zones).
 * La fórmula: bw = pixelesDisponibles / (chars * 13 + 2 * quietZone)
 *
 * @param {string} value  - El string a codificar
 * @param {number} pxW    - Ancho disponible en píxeles (ya escalado con SX)
 * @param {object} opts   - { bhDefault, fsDefault, bhMin, fsMin, bwMax }
 */
const calcBarParams = (value, pxW, opts = {}) => {
  const {
    bhDefault = 28,
    fsDefault = 10,
    bhMin     = 16,
    fsMin     = 7,
    bwMax     = 1.6,
  } = opts;

  const len = (value || "").length || 1;
  // CODE39: ~13 módulos por char + ~20 de quiet zones totales
  const modulos = len * 13 + 20;
  // bw calculado para que el código entre justo, con un 90% de uso del ancho
  const bwCalc = (pxW * 0.90) / modulos;
  const bw = Math.min(Math.max(parseFloat(bwCalc.toFixed(2)), 0.5), bwMax);

  // Escalar font size proporcionalmente: texto más largo → fuente más chica
  // Base: longitud 11 chars → fsDefault; cada 3 chars adicionales → -1pt
  const extraChars = Math.max(0, len - 11);
  const fs = Math.max(fsMin, fsDefault - Math.floor(extraChars / 3));

  // Escalar altura si hay poco espacio horizontal (barcodes muy comprimidos)
  const bh = bw < 0.8 ? bhMin : bhDefault;

  return { bw, bh, fs };
};

const formatId = (n) => {
  const s = String(n || "").replace(/-/g, "");
  if (s.length === 17) return `${s.slice(0,2)}-${s.slice(2,4)}-${s.slice(4,10)}-${s.slice(10)}`;
  return n;
};

// Modificación clave: Se agrega el prop "format" para poder forzar ITF o CODE128 según corresponda
const BCPos = ({ top, left, width, height, value, bw = 1.2, bh = 28, fs = 7, format = "CODE128" }) => (
  <div style={{
    position: "absolute",
    top: top * SY, left: left * SX,
    width: width * SX, height: height * SY,
    zIndex: 50, overflow: "hidden",
    display: "flex", alignItems: "center", justifyContent: "center",
  }}>
    <Barcode value={value || "0000000000"} width={bw} height={bh} fontSize={fs} format={format}
      margin={0} background="transparent" displayValue={true} />
  </div>
);

// ══════════════════════════════════════════════════════════════
// HTML DE CRYSTAL REPORTS CON CORRECCIONES
// ══════════════════════════════════════════════════════════════
const generarHTML = ({
  titulo, contribuyente, fechaEmision, vencimiento,
  importeNominal, descuento, totalAPagar,
  idFmt, domEntrega, conceptoItem,
  codPagoElec, colectorNombre, etiquetaTalon, numeroBoleta
}) => `
<style>
  div { position:absolute; z-index:25; box-sizing:border-box; }
  .f1  { font-size:6pt;  color:#000; font-family:Arial; font-weight:normal; }
  .f2  { font-size:7pt;  color:#000; font-family:Arial; font-weight:normal; }
  .f4  { font-size:8pt;  color:#000; font-family:Arial; font-weight:normal; }
  .f5  { font-size:13pt; color:#000; font-family:Arial; font-weight:bold; font-style:italic; }
  .f6  { font-size:8pt;  color:#000; font-family:Arial; font-weight:bold; }
  .f8  { font-size:8pt;  color:#000; font-family:"Trebuchet MS"; font-weight:normal; }
  .f9  { font-size:7pt;  color:#000; font-family:Arial; font-weight:bold; }
  .f10 { font-size:10pt; color:#000; font-family:Arial; font-weight:normal; }
  .f11 { font-size:11pt; color:#000; font-family:Arial; font-weight:bold; }
  .f12 { font-size:8pt;  color:#000; font-family:Arial; font-weight:bold; font-style:italic; }
  .f13 { font-size:9pt;  color:#000; font-family:Arial; font-weight:bold; }
  .f14 { font-size:6pt;  color:#000; font-family:Arial; font-weight:bold; }
  .b0  { border:none; }
  .b1  { border:1px solid #808080; }
  .b4  { border:1px solid #000; }
</style>

<div style="z-index:10;top:162px;left:9px;width:365px;height:35px;border:1px solid #808080;background:#c0c0c0;"></div>
<div style="z-index:10;top:198px;left:9px;width:365px;height:59px;border:1px solid #000;"></div>
<div style="z-index:10;top:271px;left:9px;width:365px;height:63px;border:1px solid #000;background:#c0c0c0;"></div>
<div style="z-index:10;top:913px;left:11px;width:238px;height:40px;border:1px solid #000;"></div>
<div style="z-index:10;top:913px;left:267px;width:238px;height:40px;border:1px solid #000;"></div>
<div style="z-index:10;top:913px;left:520px;width:238px;height:40px;border:1px solid #000;"></div>
<div style="z-index:10;top:891px;left:70px;width:179px;height:22px;border:1px solid #000;background:#c0c0c0;"></div>
<div style="z-index:10;top:891px;left:326px;width:179px;height:22px;border:1px solid #000;background:#c0c0c0;"></div>
<div style="z-index:10;top:891px;left:579px;width:179px;height:22px;border:1px solid #000;background:#c0c0c0;"></div>

<div style="z-index:15;top:97px;left:10px;border-top:2px solid #000;width:748px;height:0;"></div>
<div style="z-index:15;top:171px;left:551px;border-top:1px solid #000;width:205px;height:0;"></div>
<div style="z-index:15;top:227px;left:554px;border-top:1px solid #000;width:200px;height:0;"></div>
<div style="z-index:15;top:281px;left:555px;border-top:1px solid #000;width:197px;height:0;"></div>
<div style="z-index:15;top:309px;left:555px;border-top:1px solid #000;width:197px;height:0;"></div>
<div style="z-index:15;top:334px;left:556px;border-top:1px solid #000;width:197px;height:0;"></div>
<div style="z-index:15;top:354px;left:9px;border-top:1px solid #000;width:367px;height:0;"></div>
<div style="z-index:15;top:377px;left:10px;border-top:1px solid #000;width:365px;height:0;"></div>

<div style="z-index:15;top:461px;left:457px;border-left:1px solid #000;width:0;height:40px;"></div>
<div style="z-index:15;top:461px;left:544px;border-left:1px solid #000;width:0;height:40px;"></div>
<div style="z-index:15;top:461px;left:646px;border-left:1px solid #000;width:0;height:40px;"></div>

<div style="z-index:15;top:661px;left:2px;border-top:1px dotted #000;width:759px;height:0;"></div>
<div style="z-index:15;top:663px;left:258px;border-left:1px dotted #000;width:0;height:430px;"></div>
<div style="z-index:15;top:660px;left:511px;border-left:1px dotted #000;width:0;height:431px;"></div>
<div style="z-index:15;top:778px;left:8px;border-top:1px solid #000;width:245px;height:0;"></div>
<div style="z-index:15;top:778px;left:264px;border-top:1px solid #000;width:245px;height:0;"></div>
<div style="z-index:15;top:778px;left:517px;border-top:1px solid #000;width:245px;height:0;"></div>
<div style="z-index:15;top:892px;left:70px;border-left:1px solid #000;width:0;height:62px;"></div>
<div style="z-index:15;top:892px;left:129px;border-left:1px solid #000;width:0;height:61px;"></div>
<div style="z-index:15;top:892px;left:190px;border-left:1px solid #000;width:0;height:61px;"></div>
<div style="z-index:15;top:892px;left:326px;border-left:1px solid #000;width:0;height:62px;"></div>
<div style="z-index:15;top:892px;left:385px;border-left:1px solid #000;width:0;height:61px;"></div>
<div style="z-index:15;top:892px;left:446px;border-left:1px solid #000;width:0;height:61px;"></div>
<div style="z-index:15;top:892px;left:579px;border-left:1px solid #000;width:0;height:62px;"></div>
<div style="z-index:15;top:892px;left:638px;border-left:1px solid #000;width:0;height:61px;"></div>
<div style="z-index:15;top:892px;left:699px;border-left:1px solid #000;width:0;height:61px;"></div>

<div style="z-index:25;top:1px;left:21px;width:94px;height:94px;">
  <img src="/logo_pocito.png" width="94" height="94" style="object-fit:contain;" onerror="this.style.display='none'">
</div>

<div class="b0" style="z-index:25;top:18px;left:121px;width:380px;height:30px;">
  <span class="f5">Municipalidad de Pocito</span>
</div>
<div class="b0" style="z-index:25;top:52px;left:121px;width:300px;height:20px;">
  <span class="f4">Roberto Vidart 1073 -Oeste-</span>
</div>

<div style="z-index:5;top:111px;left:176px;width:409px;height:27px;background:#808080;"></div>
<div class="b1" style="z-index:25;top:107px;left:172px;width:409px;height:27px;background:#fff;display:flex;align-items:center;justify-content:center;">
  <span class="f5">${titulo}</span>
</div>

<div class="b0" style="z-index:25;top:166px;left:12px;width:360px;height:28px;display:flex;align-items:center;overflow:hidden;">
  <span class="f5">${contribuyente?.nombre || ""}</span>
</div>

<div class="b0" style="z-index:25;top:150px;left:555px;width:103px;height:13px;">
  <span class="f4">Fecha de Emisión:</span>
</div>
<div class="b0" style="z-index:25;top:150px;left:687px;width:67px;height:13px;text-align:right;">
  <table width="67px"><tr><td align="right"><span class="f4">${fechaEmision}</span></td></tr></table>
</div>

<div class="b0" style="z-index:25;top:199px;left:16px;width:66px;height:13px;">
  <span class="f4">Domicilio:</span>
</div>
<div class="b0" style="z-index:25;top:205px;left:14px;width:358px;height:52px;overflow:hidden;display:flex;align-items:flex-start;padding-top:14px;line-height:1.3;">
  <span class="f6">${contribuyente?.direccion_real || ""}</span>
</div>

<div class="b0" style="z-index:25;top:272px;left:16px;width:117px;height:13px;">
  <span class="f4">Domicilio de Entrega:</span>
</div>
<div class="b0" style="z-index:25;top:278px;left:14px;width:358px;height:56px;overflow:hidden;display:flex;align-items:flex-start;padding-top:14px;line-height:1.3;">
  <span class="f6">${domEntrega}</span>
</div>

<div class="b0" style="z-index:25;top:181px;left:568px;width:175px;height:16px;text-align:center;">
  <table width="175px"><td align="center"><span class="f4">ID Contribuyente</span></td></table>
</div>
<div class="b0" style="z-index:25;top:196px;left:551px;width:207px;height:20px;text-align:center;">
  <table width="207px"><tr><td align="center"><span class="f13">${idFmt}</span></td></tr></table>
</div>

<div class="b0" style="z-index:25;top:235px;left:555px;width:197px;height:15px;text-align:center;">
  <table width="197px"><td align="center"><span class="f4">Código </span><span class="f6">PAGO ELECTRONICO</span></td></table>
</div>
<div class="b0" style="z-index:25;top:251px;left:551px;width:207px;height:18px;text-align:center;">
  <table width="207px"><tr><td align="center"><span class="f13">${codPagoElec}</span></td></tr></table>
</div>

<div class="b0" style="z-index:25;top:281px;left:585px;width:197px;height:15px;">
  <span class="f4">Boleta Nro:&nbsp;</span><span class="f6">${numeroBoleta}</span>
</div>

<div class="b0" style="z-index:25;top:308px;left:555px;width:197px;height:13px;text-align:center;">
  <table width="197px"><td align="center"><span class="f8">Control de Distribución</span></td></table>
</div>

<div class="b0" style="z-index:25;top:358px;left:15px;width:348px;height:14px;display:flex;align-items:center;white-space:nowrap;overflow:hidden;">
  <span class="f2"><b>COLECTOR:</b>&nbsp;${colectorNombre}</span>
</div>

<div class="b0" style="z-index:25;top:466px;left:10px;width:185px;height:80px;line-height:1.6;">
  <span class="f4">Esta <b>BOLETA</b> puede ser<br>abonada en:<br>MUNICIPALIDAD<br>COLECTORES MUNICIPALES<br>BANCO SAN JUAN</span>
</div>

<div style="z-index:20;top:461px;left:203px;width:549px;height:20px;background:#c0c0c0;border:1px solid #000;overflow:hidden;">
  <div style="position:absolute;left:4px;top:-5px;"><span class="f9">CONCEPTOS DE COBRO</span></div>
</div>
<div style="z-index:20;top:481px;left:203px;width:549px;height:20px;border-left:1px solid #000;border-right:1px solid #000;border-bottom:1px solid #000;overflow:hidden;">
  <div style="position:absolute;left:4px;top:-3px;"><span class="f4">Descripción</span></div>
  <div style="position:absolute;right:4px;top:-3px;"><span class="f4">Total Nominal</span></div>
</div>
<div class="b0" style="z-index:25;top:500px;left:207px;width:390px;height:20px;overflow:hidden;">
  <span class="f4">${conceptoItem.includes(" ") ? conceptoItem.substring(0, conceptoItem.lastIndexOf(" ")) : conceptoItem}</span>
</div>
<div class="b0" style="z-index:25;top:500px;left:350px;width:390px;height:20px;overflow:hidden;">
  <span class="f4">${conceptoItem.includes(" ") ? conceptoItem.split(" ").pop() : ""}</span>
</div>
<div class="b0" style="z-index:25;top:500px;left:600px;width:148px;height:14px;text-align:right;">
  <table width="148px"><tr><td align="right"><span class="f4">$ ${fmt(importeNominal)}</span></td></tr></table>
</div>

<div style="z-index:20;top:537px;left:203px;width:549px;height:20px;background:#c0c0c0;border:1px solid #000;overflow:hidden;">
  <div style="position:absolute;left:4px;top:-4px;width:248px;"><span class="f9">FORMAS DE PAGO</span></div>
  <div style="position:absolute;left:252px;top:0;width:1px;height:20px;background:#000;"></div>
  <div style="position:absolute;left:253px;top:-4px;width:88px;text-align:center;"><span class="f9">Vencimiento</span></div>
  <div style="position:absolute;left:341px;top:0;width:1px;height:20px;background:#000;"></div>
  <div style="position:absolute;left:342px;top:-4px;width:101px;text-align:center;"><span class="f9">Descuento</span></div>
  <div style="position:absolute;left:443px;top:0;width:1px;height:20px;background:#000;"></div>
  <div style="position:absolute;left:444px;top:-4px;width:105px;text-align:center;"><span class="f9">Total a Pagar</span></div>
</div>
<div style="z-index:20;top:557px;left:203px;width:549px;height:34px;border:1px solid #000;overflow:hidden;">
  <div style="position:absolute;left:4px;top:50%;transform:translateY(-50%);width:248px;"><span class="f10">Importe con Descuento</span></div>
  <div style="position:absolute;left:252px;top:0;width:1px;height:34px;background:#000;"></div>
  <div style="position:absolute;left:253px;top:50%;transform:translateY(-50%);width:88px;text-align:center;"><span class="f10">${vencimiento}</span></div>
  <div style="position:absolute;left:341px;top:0;width:1px;height:34px;background:#000;"></div>
  <div style="position:absolute;right:106px;top:50%;transform:translateY(-50%);width:102px;text-align:right;padding-right:4px;"><span class="f13">$&nbsp;</span><span class="f11">${fmt(descuento)}</span></div>
  <div style="position:absolute;left:443px;top:0;width:1px;height:34px;background:#000;"></div>
  <div style="position:absolute;right:4px;top:50%;transform:translateY(-50%);width:106px;text-align:right;"><span class="f13">$&nbsp;</span><span class="f11">${fmt(totalAPagar)}</span></div>
</div>

<div class="b4" style="z-index:25;top:600px;left:10px;width:168px;height:50px;display:flex;align-items:center;justify-content:center;">
  <div style="text-align:center;line-height:1.35;">
    <span class="f12">"DONAR ORGANOS ES<br>SALVAR VIDAS"</span>
  </div>
</div>

<div class="b0" style="z-index:25;top:630px;left:618px;width:134px;height:12px;text-align:right;">
  <table width="134px"><td align="right"><span class="f1">TALON CONTRIBUYENTE</span></td></table>
</div>

${[12, 266, 522].map(l => `
<div style="z-index:25;top:666px;left:${l}px;width:45px;height:45px;">
  <img src="/logo_pocito.png" width="45" height="45" style="object-fit:contain;" onerror="this.style.display='none'">
</div>`).join("")}

${[62, 316, 570].map(l => `
<div class="b0" style="z-index:25;top:672px;left:${l}px;width:195px;height:13px;">
  <span class="f12">Municipalidad de Pocito</span>
</div>`).join("")}

<div style="z-index:5;top:718px;left:57px;width:190px;height:18px;background:#808080;"></div>
<div class="b1" style="z-index:25;top:714px;left:53px;width:190px;height:18px;background:#fff;display:flex;align-items:center;justify-content:center;">
  <span class="f12">${titulo}</span>
</div>
<div style="z-index:5;top:718px;left:309px;width:192px;height:18px;background:#808080;"></div>
<div class="b1" style="z-index:25;top:714px;left:305px;width:192px;height:18px;background:#fff;display:flex;align-items:center;justify-content:center;">
  <span class="f12">${titulo}</span>
</div>
<div style="z-index:5;top:718px;left:563px;width:192px;height:18px;background:#808080;"></div>
<div class="b1" style="z-index:25;top:714px;left:559px;width:192px;height:18px;background:#fff;display:flex;align-items:center;justify-content:center;">
  <span class="f12">${titulo}</span>
</div>

${[[13,104],[269,360],[522,613]].map(([lbl,val]) => `
<div class="b0" style="z-index:25;top:730px;left:${lbl}px;width:89px;height:13px;">
  <span class="f2">Fecha de Emisión:</span>
</div>
<div class="b0" style="z-index:25;top:730px;left:${val}px;width:88px;height:13px;">
  <span class="f2">${fechaEmision}</span>
</div>`).join("")}

${[10,264,518].map(l => `
<div class="b0" style="z-index:25;top:748px;left:${l}px;width:240px;height:25px;overflow:hidden;line-height:1.2;">
  <span class="f1">${conceptoItem}</span>
</div>`).join("")}

${[[11,87],[267,343],[520,596]].map(([lbl,val]) => `
<div class="b0" style="z-index:25;top:786px;left:${lbl}px;width:76px;height:16px;">
  <span class="f1">ID Contribuyente:</span>
</div>
<div class="b0" style="z-index:25;top:786px;left:${val}px;width:166px;height:25px;overflow:hidden;">
  <span class="f1">${idFmt}</span>
</div>`).join("")}

${[[11,83],[267,339],[520,592]].map(([lbl,val]) => `
<div class="b0" style="z-index:25;top:809px;left:${lbl}px;width:72px;height:16px;">
  <span class="f1">Contribuyente:</span>
</div>
<div class="b0" style="z-index:25;top:809px;left:${val}px;width:175px;height:25px;overflow:hidden;white-space:nowrap;">
  <span class="f1">${contribuyente?.nombre || ""}</span>
</div>`).join("")}

${[[10,10],[266,266],[523,523]].map(([lbl,val]) => `
<div class="b0" style="z-index:25;top:831px;left:${lbl}px;width:41px;height:16px;">
  <span class="f1">Domicilio:</span>
</div>
<div class="b0" style="z-index:25;top:846px;left:${val}px;width:244px;height:60px;overflow:hidden;line-height:0.80;">
  <span class="f1">${contribuyente?.direccion_real || ""}</span>
</div>`).join("")}

${[[74,136,211],[330,392,467],[583,645,720]].map(([v,d,t]) => `
<div class="b0" style="z-index:25;top:885px;left:${v}px;width:54px;height:12px;text-align:center;">
  <span class="f1">Vencimiento</span>
</div>
<div class="b0" style="z-index:25;top:885px;left:${d}px;width:49px;height:12px;text-align:center;">
  <span class="f1">Descuento</span>
</div>
<div class="b0" style="z-index:25;top:885px;left:${t}px;width:34px;height:12px;text-align:center;">
  <span class="f1">Total</span>
</div>`).join("")}

${[12,268,522].map(l => `
<div class="b0" style="z-index:25;top:920px;left:${l}px;width:57px;height:11px;text-align:center;">
  <span class="f1">${etiquetaTalon}</span>
</div>`).join("")}

${[73,329,582].map(l => `
<div class="b0" style="z-index:25;top:920px;left:${l}px;width:54px;height:13px;text-align:center;">
  <table width="54px"><tr><td align="center"><span class="f1">${vencimiento}</span></td></tr></table>
</div>`).join("")}

${[[132,131],[388,388],[641,641]].map(([s,v]) => `
<div class="b0" style="z-index:25;top:920px;left:${s}px;width:9px;height:13px;">
  <span class="f14">$</span>
</div>
<div class="b0" style="z-index:25;top:920px;left:${v}px;width:56px;height:13px;text-align:right;">
  <table width="56px"><tr><td align="right"><span class="f1">${fmt(descuento)}</span></td></tr></table>
</div>`).join("")}

${[[193,194],[449,450],[702,703]].map(([s,v]) => `
<div class="b0" style="z-index:25;top:920px;left:${s}px;width:9px;height:13px;">
  <span class="f14">$</span>
</div>
<div class="b0" style="z-index:25;top:920px;left:${v}px;width:56px;height:13px;text-align:right;">
  <table width="56px"><tr><td align="right"><span class="f1">${fmt(totalAPagar)}</span></td></tr></table>
</div>`).join("")}

${[[10,52],[266,308],[519,561]].map(([lbl,val]) => `
<div class="b0" style="z-index:25;top:979px;left:${lbl}px;width:40px;height:12px;">
  <span class="f1">Colector:</span>
</div>
<div class="b0" style="z-index:25;top:979px;left:${val}px;width:200px;height:12px;overflow:hidden;">
  <span class="f1">${colectorNombre}</span>
</div>`).join("")}

<div class="b0" style="z-index:25;top:1075px;left:10px;width:248px;height:16px;text-align:center;">
  <table width="248px"><tr><td align="center"><span class="f9">TESORERÍA</span></td></tr></table>
</div>
<div class="b0" style="z-index:25;top:1075px;left:262px;width:248px;height:16px;text-align:center;">
  <table width="248px"><tr><td align="center"><span class="f9">RENTAS</span></td></tr></table>
</div>
<div class="b0" style="z-index:25;top:1075px;left:514px;width:248px;height:16px;text-align:center;">
  <table width="248px"><tr><td align="center"><span class="f9">ENTIDAD COBRO</span></td></tr></table>
</div>
`;

// ══════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════
const BoletaView = () => {
  const { numero } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);

  useEffect(() => {
    axios.get(`${API}/boleta/${numero}`)
      .then((r) => setData(r.data))
      .catch((e) => { console.error(e); setError("No se pudo cargar la boleta."); })
      .finally(() => setLoading(false));
  }, [numero]);

  if (loading) return (
    <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", background:"#f1f5f9" }}>
      <Loader2 style={{ width:48, height:48, color:"#2563eb", marginBottom:16 }} className="animate-spin" />
      <p style={{ color:"#475569", fontWeight:500 }}>Cargando boleta...</p>
    </div>
  );

  if (error || !data) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#f1f5f9" }}>
      <div style={{ background:"#fef2f2", color:"#dc2626", padding:32, borderRadius:12, border:"1px solid #fecaca", textAlign:"center" }}>
        <h2 style={{ fontSize:22, fontWeight:"bold", marginBottom:8 }}>Error</h2>
        <p>{error}</p>
        <button onClick={() => window.close()} style={{ marginTop:24, padding:"8px 20px", borderRadius:6, cursor:"pointer", border:"1px solid #dc2626", background:"white" }}>Cerrar</button>
      </div>
    </div>
  );

  const { boleta, contribuyente, items } = data;
  const importeNominal   = parseFloat(boleta.IMPORTE || boleta.DEUDA || 0);
  const descuento        = parseFloat(boleta.DTOESP || 0) + parseFloat(boleta.DTOPRPG || 0);
  const totalAPagar      = parseFloat(boleta.TOTAL || boleta.TOTALGRAL || 0);
  const vencimiento      = fmtFecha(boleta.FECHAVTO1 || boleta.FECHAVTO || "");
  const fechaEmision     = fmtFecha(boleta.FECHAEMIS || boleta.FECHADEUDA || "");
  const titulo           = getTitulo(boleta.TIPO, boleta.PERIODO, boleta.CONCEPTO);
  const codCuerpo        = getCodCuerpo(boleta);
  const codInterbancario = getCodInterbancario(boleta);
  const codNomenclatura  = String(boleta.NOMENCLA || "").replace(/-/g, "");
  const codPagoElec      = getCodPagoElec(boleta.NOMENCLA);
  const idFmt            = formatId(boleta.NOMENCLA);
  const domEntrega       = contribuyente?.domicilio_postal || contribuyente?.direccion_real || "";
  const conceptoItem     = items?.length > 0
    ? items.map((i) => `${i.CONCEPTO} ${parseFloat(i.VALORUNITARIO||0).toFixed(2)}`).join(" / ")
    : boleta.CONCEPTO || "";
  const colectorNombre   = `${boleta.COLECTOR||""} ${boleta.NOMBRECOLECTOR||""} TEL.: ${boleta.CELULAR||""}`;

  // Determinar si es boleta mensual (CODBARRASM con formato *P...*)
  const esMensual = String(boleta.CODBARRASM || "").trim().startsWith("*");

  // Etiqueta dinámica para fila de tabla en talones
  const tipoBol    = String(boleta.TIPO || "").toUpperCase().trim();
  const periodoStr = String(boleta.PERIODO || "");
  const etiquetaTalon = tipoBol === "A" ? "Pago Anual"
    : tipoBol === "1" ? "1° Semestre"
    : tipoBol === "2" ? "2° Semestre"
    : periodoStr.length >= 6
      ? `Cuota ${periodoStr.substring(4,6)}/${periodoStr.substring(0,4)}`
      : "Cuota";

  const numeroBoleta = String(boleta.BOLETA || "");

  const htmlContent = generarHTML({
    titulo, contribuyente, fechaEmision, vencimiento,
    importeNominal, descuento, totalAPagar,
    idFmt, domEntrega, conceptoItem,
    codPagoElec, colectorNombre, etiquetaTalon, numeroBoleta
  });

  return (
    <div id="boleta-page" style={{ minHeight:"100vh", background:"#e2e8f0", padding:"20px 0" }}>

      {/* Botonera - visible en pantalla, oculta al imprimir */}
      <div id="botonera-boleta" style={{
        maxWidth:PAGE_W, margin:"0 auto 14px", display:"flex",
        justifyContent:"space-between", padding:"0 6px",
        position:"relative", zIndex:9999,
      }}>
        <button
          onClick={() => window.history.length > 1 ? window.history.back() : window.close()}
          style={{
            display:"inline-flex", alignItems:"center", gap:6,
            padding:"10px 20px", borderRadius:8,
            background:"#fff", color:"#334155", border:"1px solid #cbd5e1",
            fontSize:14, fontWeight:600, cursor:"pointer",
            boxShadow:"0 1px 3px rgba(0,0,0,0.1)", fontFamily:"Arial, sans-serif",
          }}
        >
          <ArrowLeft style={{ width:16, height:16 }} /> Volver
        </button>
        <button
          onClick={() => window.print()}
          style={{
            display:"inline-flex", alignItems:"center", gap:6,
            padding:"10px 24px", borderRadius:8,
            background:"#1d4ed8", color:"#fff", border:"none",
            fontSize:14, fontWeight:700, cursor:"pointer",
            boxShadow:"0 2px 6px rgba(29,78,216,0.4)", fontFamily:"Arial, sans-serif",
          }}
        >
          <Printer style={{ width:18, height:18 }} /> Imprimir Boleta
        </button>
      </div>

      {/* HOJA A4 */}
      <div id="hoja-a4" style={{ position:"relative", width:PAGE_W, height:PAGE_H, margin:"0 auto", background:"white", boxShadow:"0 4px 24px rgba(0,0,0,0.25)", overflow:"hidden" }}>

        {/* Contenido escalado con margen seguro para impresión */}
        <div id="contenido-imprimible" style={{
          position:"absolute",
          top: MARGIN, left: MARGIN,
          width: PAGE_W, height: PAGE_H,
          transformOrigin: "top left",
          transform: `scale(${PRINT_SCALE})`,
        }}>

          {/* HTML CR escalado */}
          <div style={{
            position:"absolute", top:0, left:0,
            width:CR_W, height:CR_H,
            transformOrigin:"top left",
            transform:`scale(${SX}, ${SY})`,
            background:"white",
          }}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />

          {/* ══ BARCODES HOMOLOGADOS ══ */}

          {/* 2. Control de Distribución — CODE39, ancho dinámico según nomenclatura */}
          {(() => {
            // bwMax = 1.3 deriva de la referencia visual correcta (17 chars, ej: IM202006500000000).
            // Nomenclaturas cortas (CO100, 5 chars) calculan hacia abajo para mantener
            // el mismo aspecto proporcional — nunca superan ese techo.
            const { bw, bh, fs } = calcBarParams(codNomenclatura, 146 * SX, {
              bhDefault: 40,
              fsDefault: 12,
              bhMin:     30,
              fsMin:     12,
              bwMax:     1.6,
            });
            return (
              <BCPos top={330} left={587} width={146} height={60}
                value={codNomenclatura} format="CODE39" bw={bw} bh={bh} fs={fs} />
            );
          })()}

          {/* 3. Barcode izquierdo (debajo colector) — CODE39, ancho dinámico según tipo */}
          {(() => {
            const { bw, bh, fs } = calcBarParams(codCuerpo, 255 * SX, {
              bhDefault: 40, fsDefault: 12, bhMin: 30, fsMin: 12, bwMax: 1.6,
            });
            return (
              <BCPos top={385} left={40} width={255} height={60}
                value={codCuerpo} format="CODE39" bw={bw} bh={bh} fs={fs} />
            );
          })()}

          {/* 4. Barcode interbancario central — ITF, 9.5cm × 0.9cm */}
          {codInterbancario && (
            <BCPos top={585} left={285} width={346} height={70}
              value={codInterbancario} format="ITF" bw={1.3} bh={40} fs={16} />
          )}

          {/* 5. Barcodes talones — CODE39, ancho dinámico según tipo de boleta */}
          {(() => {
            const { bw, bh, fs } = calcBarParams(codCuerpo, 230 * SX, {
              bhDefault: 30, fsDefault: 14, bhMin: 20, fsMin:12, bwMax: 1.3,
            });
            return [11, 268, 520].map((left, i) => (
              <BCPos key={`talon-${i}`} top={1005} left={left} width={230} height={60}
                value={codCuerpo} format="CODE39" bw={bw} bh={bh} fs={fs} />
            ));
          })()}

        </div>{/* fin contenido-imprimible */}
      </div>

      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 0;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            overflow: hidden !important;
          }
          #botonera-boleta {
            display: none !important;
          }
          #boleta-page {
            min-height: 0 !important;
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          #hoja-a4 {
            box-shadow: none !important;
            margin: 0 !important;
            width: 210mm !important;
            height: 297mm !important;
            overflow: hidden !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BoletaView;